import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../../utils/api";
import { ODO, ODOResponse } from "../../types/cs";

interface UseODOParams {
  pageSize?: number;
}

interface UseODOReturn {
  odos: ODO[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

const useODO = (params: UseODOParams = {}): UseODOReturn => {
  const { pageSize = 20 } = params;
  
  const [odos, setOdos] = useState<ODO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchODOs = useCallback(async (page: number = 1, isAppend: boolean = false) => {
    if (page === 1) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    setError("");

    try {
      const response = await apiClient.get("/api/v1/cs/odo", { 
        params: { page: page }
      });
      
      const responseData: ODOResponse = response.data;

      if (responseData.status) {
        const newODOs = responseData.data;
        
        if (isAppend) {
          setOdos(prev => [...prev, ...newODOs]);
        } else {
          setOdos(newODOs);
        }

        setHasMore(newODOs.length >= pageSize);
        setCurrentPage(page);
      } else {
        setError("1:1 문의를 불러오는데 실패했습니다.");
      }
    } catch (err) {
      console.error("1:1 문의 조회 오류:", err);
      setError("네트워크 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [pageSize]);

  const loadMore = useCallback(async () => {
    if (hasMore && !isLoadingMore && !isLoading) {
      await fetchODOs(currentPage + 1, true);
    }
  }, [hasMore, isLoadingMore, isLoading, currentPage, fetchODOs]);

  const refresh = useCallback(async () => {
    setCurrentPage(1);
    setHasMore(true);
    await fetchODOs(1, false);
  }, [fetchODOs]);

  useEffect(() => {
    fetchODOs(1, false);
  }, [fetchODOs]);

  return {
    odos,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    loadMore,
    refresh,
  };
};

export default useODO;
export type { UseODOParams, UseODOReturn }; 